import { getEnumLookup } from "src/app/_helpers/enum-helper";
import { ComplianceStateDisplayMap, ComplianceStateEnum } from "src/app/enums/complianceState.enum";
import { RiskRegisterAssessmentStatusEnum } from "src/app/enums/assessmentStatus.enum";
import { ColumnFilter, FilterOperator } from "src/app/models/columnFilter.model";
import { DisplayGridColumnSettings } from "src/app/models/gridColumnSettings.model";
import { ConsequenceRating } from "src/app/models/jurisdictionRiskConfig.model";
import { AssessmentMethodEnum, LikelihoodColorLevel, RiskLevel, RiskRegister } from "src/app/models/risk-assessment.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import { RiskRegisterCapabilityProfile } from "./risk-register-capability-profile";

export enum RiskRegisterFilterKind {
    Options = "options",
    Range = "range",
}

export const RISK_REGISTER_FILTER_KEYS = {
    status: nameOf<RiskRegister>((x) => x.assessmentStatus),
    method: nameOf<RiskRegister>((x) => x.methodOfAssessment),
    compliance: nameOf<RiskRegister>((x) => x.complianceState),
    consequence: nameOf<RiskRegister>((x) => x.consequenceRating),
    controlAdequate: "ControlAdequate",
    riskRating: "RiskRating",
    numberOfSamples: "NumberOfSamples",
    healthRiskLevel: "HealthRiskLevel",
    likelihoodLevel: "LikelihoodLevel",
    exposureBandLevel: "ExposureBandLevel",
} as const;

const LEGACY_CONTROL_KEY = nameOf<RiskRegister>((x) => x.isControlAdequate);
const NOT_APPLICABLE_ORDINAL = 0;
const RANGE_PATTERN = /^(-?\d*),(-?\d*)$/;
const RANGE_OPERATORS: string[] = [FilterOperator.Between, FilterOperator.GreaterThanOrEqual, FilterOperator.LessThanOrEqual];

export const RISK_REGISTER_RANGE_OPERATORS = [
    { value: FilterOperator.Between, label: "is between" },
    { value: FilterOperator.GreaterThanOrEqual, label: "is more than or equal to" },
    { value: FilterOperator.LessThanOrEqual, label: "is less than or equal to" },
];

export type RiskRegisterFilterState = Record<string, string>;

export interface RiskRegisterFilterOption {
    value: number;
    label: string;
    hint?: string;
}

export interface RiskRegisterFilterFieldDef {
    key: string;
    label: string;
    kind: RiskRegisterFilterKind;
    options: RiskRegisterFilterOption[];
}

export interface RiskRegisterFilterContext {
    profile: RiskRegisterCapabilityProfile;
    exposureBand: boolean;
    riskLevels: RiskLevel[];
    likelihoodLevels: LikelihoodColorLevel[];
    consequenceRatings: ConsequenceRating[];
}

export interface RiskRegisterFilterChip {
    key: string;
    label: string;
    value: string;
}

export interface RiskRegisterFilterRow {
    def: RiskRegisterFilterFieldDef;
    filter: ColumnFilter;
}

export function formatThresholdRange(min?: number, max?: number, suffix = ""): string {
    const hasMin = Number.isFinite(min);
    const hasMax = Number.isFinite(max);
    if (hasMin && hasMax) {
        return `${min}–${max}${suffix}`;
    }
    return hasMin ? `≥${min}${suffix}` : "";
}

function optionsField(key: string, label: string, options: RiskRegisterFilterOption[]): RiskRegisterFilterFieldDef {
    return { key, label, kind: RiskRegisterFilterKind.Options, options };
}

function rangeField(key: string, label: string): RiskRegisterFilterFieldDef {
    return { key, label, kind: RiskRegisterFilterKind.Range, options: [] };
}

function enumOptions(source: Record<string, string | number>): RiskRegisterFilterOption[] {
    return getEnumLookup(source).map((item) => ({ value: item.id, label: item.name }));
}

function levelOptions(
    levels: { name?: string; range: string }[],
    notApplicableLabel: string,
): RiskRegisterFilterOption[] {
    const named = levels.flatMap((level, index) =>
        level.name?.trim() ? [{ value: index + 1, label: level.name.trim(), hint: level.range || undefined }] : [],
    );
    return [...named, { value: NOT_APPLICABLE_ORDINAL, label: notApplicableLabel }];
}

export function buildFilterFields(context: RiskRegisterFilterContext): RiskRegisterFilterFieldDef[] {
    const { profile, exposureBand } = context;
    const keys = RISK_REGISTER_FILTER_KEYS;
    const exposure = profile.usesExposureAssessment;
    const healthLevels = levelOptions(
        context.riskLevels.map((level) => ({ name: level.Name, range: formatThresholdRange(level.Thresholds?.[0], level.Thresholds?.[1]) })),
        "N/A",
    );
    const likelihoodLevels = levelOptions(
        context.likelihoodLevels.map((level) => ({
            name: exposureBand ? level.QuantitativeName : level.Name,
            range: formatThresholdRange(level.minQuantThreshold, level.maxQuantThreshold, "%"),
        })),
        "N/A",
    );
    const complianceOptions = Object.values(ComplianceStateEnum)
        .filter((value): value is ComplianceStateEnum => typeof value === "number")
        .map((value) => ({ value, label: ComplianceStateDisplayMap[value]?.label ?? "Not assessed" }));
    const consequenceOptions = context.consequenceRatings.map((rating) => ({
        value: rating.value,
        label: rating.label ? `${rating.value} - ${rating.label}` : String(rating.value),
    }));
    return [
        optionsField(keys.status, "Status", enumOptions(RiskRegisterAssessmentStatusEnum)),
        ...(exposure ? [optionsField(keys.method, "Method of Assessment", enumOptions(AssessmentMethodEnum))] : []),
        optionsField(keys.controlAdequate, "Control Adequate", [
            { value: 1, label: "Yes" },
            { value: 2, label: "No" },
            { value: 3, label: "Not set" },
        ]),
        optionsField(keys.healthRiskLevel, "Health Risk", healthLevels),
        ...(exposure ? [optionsField(keys.likelihoodLevel, exposureBand ? "Exposure Band" : "Likelihood", likelihoodLevels)] : []),
        ...(exposure ? [optionsField(keys.compliance, "EN 689 Compliance", complianceOptions)] : []),
        ...(exposure ? [optionsField(keys.consequence, "Consequence Rating", consequenceOptions)] : []),
        rangeField(keys.riskRating, "Risk Rating"),
        ...(exposure ? [rangeField(keys.numberOfSamples, "Number of Samples")] : []),
    ];
}

function migrateLegacyFilters(raw: Record<string, unknown>): Record<string, string> {
    const migrated: Record<string, string> = {};
    Object.entries(raw ?? {}).forEach(([key, value]) => {
        if (key === LEGACY_CONTROL_KEY) {
            if (value === true || value === "true") {
                migrated[RISK_REGISTER_FILTER_KEYS.controlAdequate] = "1";
            } else if (value === false || value === "false") {
                migrated[RISK_REGISTER_FILTER_KEYS.controlAdequate] = "2";
            }
        } else if (typeof value === "string" || typeof value === "number") {
            migrated[key] = String(value);
        }
    });
    return migrated;
}

function cleanOptionsValue(value: string, def: RiskRegisterFilterFieldDef): string {
    const allowed = new Set(def.options.map((option) => option.value));
    const picked = new Set(
        value
            .split(",")
            .map((token) => (token.trim() === "" ? NaN : Number(token)))
            .filter((token) => allowed.has(token)),
    );
    return [...picked].sort((left, right) => left - right).join(",");
}

function cleanRangeValue(value: string): string {
    const match = RANGE_PATTERN.exec(value.trim());
    return match && (match[1] !== "" || match[2] !== "") ? `${match[1]},${match[2]}` : "";
}

export function sanitizeFilterState(raw: Record<string, unknown>, fields: RiskRegisterFilterFieldDef[]): RiskRegisterFilterState {
    const migrated = migrateLegacyFilters(raw);
    if (migrated[RISK_REGISTER_FILTER_KEYS.exposureBandLevel] != null) {
        migrated[RISK_REGISTER_FILTER_KEYS.likelihoodLevel] ??= migrated[RISK_REGISTER_FILTER_KEYS.exposureBandLevel];
    }
    const state: RiskRegisterFilterState = {};
    fields.forEach((def) => {
        const value = migrated[def.key];
        const cleaned = value == null ? "" : def.kind === RiskRegisterFilterKind.Options ? cleanOptionsValue(value, def) : cleanRangeValue(value);
        if (cleaned) {
            state[def.key] = cleaned;
        }
    });
    return state;
}

export function omitDisabledFilters(state: RiskRegisterFilterState, disabled: ReadonlySet<string>): RiskRegisterFilterState {
    return Object.fromEntries(Object.entries(state).filter(([key]) => !disabled.has(key)));
}

export function toRequestFilters(state: RiskRegisterFilterState, exposureBand: boolean): RiskRegisterFilterState {
    const { likelihoodLevel, exposureBandLevel } = RISK_REGISTER_FILTER_KEYS;
    return Object.fromEntries(Object.entries(state).map(([key, value]) => [exposureBand && key === likelihoodLevel ? exposureBandLevel : key, value]));
}

export function hasLevelKeySwap(filters: Record<string, unknown>, previousExposureBand: boolean, nextExposureBand: boolean): boolean {
    return RISK_REGISTER_FILTER_KEYS.likelihoodLevel in filters && previousExposureBand !== nextExposureBand;
}

function formatRangeValue(value: string): string {
    const [min, max] = value.split(",");
    if (min && max) {
        return `${min}–${max}`;
    }
    return min ? `≥ ${min}` : `≤ ${max}`;
}

export function buildFilterChips(
    state: RiskRegisterFilterState,
    fields: RiskRegisterFilterFieldDef[],
    translate: (text: string) => string,
): RiskRegisterFilterChip[] {
    return fields
        .filter((def) => state[def.key])
        .map((def) => {
            const value = state[def.key];
            const text =
                def.kind === RiskRegisterFilterKind.Range
                    ? formatRangeValue(value)
                    : value
                          .split(",")
                          .map((token) => def.options.find((option) => option.value === Number(token))?.label ?? token)
                          .map(translate)
                          .join(", ");
            return { key: def.key, label: def.label, value: text };
        });
}

function toColumn(def: RiskRegisterFilterFieldDef, translate: (text: string) => string): DisplayGridColumnSettings {
    const isOptions = def.kind === RiskRegisterFilterKind.Options;
    return {
        _uid: def.key,
        property: def.key,
        displayName: translate(def.label),
        visible: true,
        order: 0,
        type: isOptions ? "string" : "number",
        isNullable: false,
        disabled: false,
        enumMap: isOptions
            ? def.options.map((option) => ({
                  value: option.value,
                  name: option.hint ? `${translate(option.label)} (${option.hint})` : translate(option.label),
              }))
            : undefined,
        isDefault: false,
        isSortable: false,
        isFilterable: true,
        nestedPath: null,
    };
}

export function toColumnFilter(
    def: RiskRegisterFilterFieldDef,
    value: string | undefined,
    translate: (text: string) => string,
): ColumnFilter {
    const column = toColumn(def, translate);
    if (def.kind === RiskRegisterFilterKind.Options) {
        return {
            column,
            operator: FilterOperator.Equal,
            valueArray: value ? value.split(",").map(Number) : [],
            search: "",
        };
    }
    const [min, max] = (value ?? ",").split(",");
    if (min && max) {
        return { column, operator: FilterOperator.Between, valueArray: [Number(min), Number(max)], search: "" };
    }
    if (min) {
        return { column, operator: FilterOperator.GreaterThanOrEqual, value: Number(min), search: "" };
    }
    return max
        ? { column, operator: FilterOperator.LessThanOrEqual, value: Number(max), search: "" }
        : { column, operator: FilterOperator.Between, valueArray: [], search: "" };
}

export function rowOperatorLabel(row: RiskRegisterFilterRow): string {
    return row.def.kind === RiskRegisterFilterKind.Range
        ? (RISK_REGISTER_RANGE_OPERATORS.find((operator) => operator.value === row.filter.operator)?.label ?? "is")
        : "is";
}

export function withFilterOperator(row: RiskRegisterFilterRow, operator: FilterOperator): RiskRegisterFilterRow {
    const filter: ColumnFilter = { ...row.filter, operator, value: undefined, valueArray: [] };
    return { def: row.def, filter };
}

export function fromColumnFilter(row: RiskRegisterFilterRow): string | null {
    const { def, filter } = row;
    if (def.kind === RiskRegisterFilterKind.Options) {
        const picked = [...new Set((filter.valueArray ?? []).map(Number))].sort((left, right) => left - right);
        return picked.length ? picked.join(",") : null;
    }
    if (!RANGE_OPERATORS.includes(filter.operator)) {
        return null;
    }
    const wholeNumber = (raw: unknown): string | undefined =>
        raw == null || raw === "" || Number.isNaN(Number(raw)) ? undefined : String(Math.round(Number(raw)));
    if (filter.operator === FilterOperator.Between) {
        const [min, max] = (filter.valueArray ?? []).map(wholeNumber);
        return min != null && max != null ? `${min},${max}` : null;
    }
    const bound = wholeNumber(filter.value);
    if (bound == null) {
        return null;
    }
    return filter.operator === FilterOperator.GreaterThanOrEqual ? `${bound},` : `,${bound}`;
}
